import React, { useEffect, useState } from 'react';
import { dataService, PPSnapshotData, CommentaryType } from '@rti/shared';
import { useTheme } from '@/contexts/ThemeContext';
import {
  normalizeRows,
  getChange,
  getSortedDates,
  getAvailableDates,
  getFirstDateWithPrelimCommentary,
  shouldShowFinalByDefault,
  formatDateLabel,
  formatCommentary,
  renderStatCard,
  hasCommentaryData
} from './snapshotUtils';
import { createChart, rsColors, formatLabel, commonChartOptions, getAxisColors } from './chartUtils';
import { Chart } from 'chart.js';
import { normalizeSnapshotResponse } from './snapshotDataUtils';
import { trackUserEvent } from '@/utils/userTracking';

interface SnapshotPPProps {
  data?: PPSnapshotData[];
  commentaryType?: 'preliminary' | 'final';
  onCommentaryTypeChange?: (type: 'preliminary' | 'final') => void;
  onSelectedDateChange?: (date: string) => void;
}

const SnapshotPP: React.FC<SnapshotPPProps> = ({ onCommentaryTypeChange, onSelectedDateChange }) => {
  const { isDark } = useTheme();
  const { tickColor, gridColor } = getAxisColors(isDark);
  const [stats, setStats] = useState<any>({});
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [commentaryTexts, setCommentaryTexts] = useState<{ supplyDemand?: string }>({});
  const [commentaryType, setCommentaryType] = useState<CommentaryType>('preliminary');
  const [chartInstances, setChartInstances] = useState<{ [key: string]: Chart | null }>({});
  const [prelimData, setPrelimData] = useState<PPSnapshotData[]>([]);
  const [finalData, setFinalData] = useState<PPSnapshotData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const currentData: PPSnapshotData[] =
    commentaryType === 'final' && finalData.length > 0 ? finalData : prelimData;

  const handleCommentaryTypeChange = (type: CommentaryType) => {
    setCommentaryType(type);
    onCommentaryTypeChange?.(type);
  };

  useEffect(() => {
    const loadBothDatasets = async () => {
      try {
        setLoading(true);
        const [prelimResp, finalResp] = await Promise.all([
          dataService.getACCPPData(),
          dataService.getACCPPDataFinal()
        ]);
        const prelimArray = normalizeSnapshotResponse<PPSnapshotData>(prelimResp) || [];
        const finalArray = normalizeSnapshotResponse<PPSnapshotData>(finalResp) || [];
        setPrelimData(prelimArray);
        setFinalData(finalArray);

        const base = finalArray.length > 0 ? finalArray : prelimArray;
        if (base.length > 0) {
          initializeFromBase(base, finalArray, prelimArray);
        }
      } finally {
        setLoading(false);
      }
    };

    loadBothDatasets();
  }, []);

  const initializeFromBase = (rows: PPSnapshotData[], finalDataArray: PPSnapshotData[], prelimDataArray: PPSnapshotData[]) => {
    const normalizedRows = normalizeRows(rows);
    const sorted = getSortedDates(normalizedRows);
    const available = getAvailableDates(sorted, 25, prelimDataArray);

    setAvailableDates(available);

    const firstDateWithCommentary = getFirstDateWithPrelimCommentary(prelimDataArray, 25);
    const defaultDate = firstDateWithCommentary || available[0] || sorted[0].date;

    const defaultRow = finalDataArray.find(f => f.date === defaultDate);
    const hasFinalCommentary = defaultRow && hasCommentaryData(defaultRow);
    const shouldUseFinal = hasFinalCommentary;

    setCommentaryType(shouldUseFinal ? 'final' : 'preliminary');
    onCommentaryTypeChange?.(shouldUseFinal ? 'final' : 'preliminary');

    setSelectedDate(defaultDate);
    onSelectedDateChange?.(defaultDate);
    updateDataForSelectedDate(defaultDate, normalizedRows);
  };

  useEffect(() => {
    if (!selectedDate || currentData.length === 0) return;
    const normalizedRows = normalizeRows(currentData);
    updateDataForSelectedDate(selectedDate, normalizedRows);
  }, [selectedDate, commentaryType, prelimData, finalData, isDark]);

  const destroyCharts = () => {
    Object.values(chartInstances).forEach(chart => {
      if (chart) {
        try {
          chart.destroy();
        } catch {}
      }
    });
    setChartInstances({});
  };

  const renderCharts = (rows: PPSnapshotData[]) => {
    destroyCharts();

    const sortedData = [...rows].sort((a, b) => new Date(a.date) > new Date(b.date) ? 1 : -1);
    if (!sortedData.length) return;

    const labels = sortedData.map(d => formatLabel(d.date));

    const domesticDemand = sortedData.map(d => +(d.domestic_demand || 0));
    const exportsTotal = sortedData.map(d => +(d.exports_total || 0));
    const capacity = sortedData.map(d => +(d.capacity || 0));
    const production = sortedData.map(d => +(d.domestic_production || 0));
    const imports = sortedData.map(d => +(d.imports || 0));
    const daysSupply = sortedData.map(d => +(d.days_of_inventory || 0));
    const inventoryChange = sortedData.map(d => +(d.inventory_change_monthly || d.inventory_change || 0));
    const operating = sortedData.map(d => +((d.operating_rates || 0) * 100).toFixed(1));

    const demandCanvas = document.getElementById('ppDemandChart') as HTMLCanvasElement | null;
    const daysCanvas = document.getElementById('ppDaysSupplyChart') as HTMLCanvasElement | null;
    const prodCanvas = document.getElementById('ppProductionChart') as HTMLCanvasElement | null;
    const inventoryCanvas = document.getElementById('ppInventoryChart') as HTMLCanvasElement | null;
    const importsCanvas = document.getElementById('ppImportsChart') as HTMLCanvasElement | null;
    const operatingCanvas = document.getElementById('ppOperatingChart') as HTMLCanvasElement | null;

    const demandChart = demandCanvas
      ? createChart(demandCanvas, {
          type: 'bar',
          data: {
            labels: labels.slice(1),
            datasets: [
              {
                label: 'Domestic',
                data: domesticDemand.slice(1),
                backgroundColor: '#42b200',
                stack: 'd'
              },
              {
                label: 'Exports',
                data: exportsTotal.slice(1),
                backgroundColor: '#E85b4c',
                stack: 'd'
              },
              {
                type: 'line' as const,
                label: 'Capacity',
                data: capacity.slice(1),
                borderColor: '#3838FF',
                fill: false,
                order: 0,
                pointRadius: 0
              }
            ]
          },
          options: {
            ...commonChartOptions,
            plugins: {
              legend: { labels: { color: tickColor } },
              datalabels: { display: false }
            },
            scales: {
              x: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } },
              y: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 }, title: { display: true, text: 'Daily lbs in 1,000s', color: tickColor, font: { size: 14 } } }
            }
          }
        })
      : null;

    const daysChart = daysCanvas
      ? createChart(daysCanvas, {
          type: 'bar',
          data: {
            labels: labels.slice(1),
            datasets: [
              {
                label: 'Days Supply',
                data: daysSupply.slice(1),
                backgroundColor: '#42b200'
              }
            ]
          },
          options: {
            ...commonChartOptions,
            plugins: {
              legend: { labels: { color: tickColor } },
              datalabels: { display: false }
            },
            scales: {
              x: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } },
              y: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 }, title: { display: true, text: 'Days', color: tickColor, font: { size: 14 } } }
            }
          }
        })
      : null;

    const prodChart = prodCanvas
      ? createChart(prodCanvas, {
          type: 'bar',
          data: {
            labels: labels.slice(1),
            datasets: [
              {
                label: 'Production',
                data: production.slice(1),
                backgroundColor: '#42b200'
              },
              {
                type: 'line' as const,
                label: 'Capacity',
                data: capacity.slice(1),
                borderColor: '#3838FF',
                fill: false,
                order: 0,
                pointRadius: 0
              }
            ]
          },
          options: {
            ...commonChartOptions,
            plugins: {
              legend: { labels: { color: tickColor } },
              datalabels: { display: false }
            },
            scales: {
              x: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } },
              y: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 }, title: { display: true, text: 'Daily lbs in 1,000s', color: tickColor, font: { size: 14 } } }
            }
          }
        })
      : null;

    const inventoryChart = inventoryCanvas
      ? createChart(inventoryCanvas, {
          type: 'bar',
          data: {
            labels: labels.slice(1),
            datasets: [
              {
                label: 'Inventory Δ',
                data: inventoryChange.slice(1),
                backgroundColor: inventoryChange.slice(1).map(v => v >= 0 ? '#42b200' : '#E85b4c')
              }
            ]
          },
          options: {
            ...commonChartOptions,
            plugins: {
              legend: { labels: { color: tickColor } },
              datalabels: { display: false }
            },
            scales: {
              x: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } },
              y: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 }, title: { display: true, text: 'Monthly lbs in 1,000s', color: tickColor, font: { size: 14 } } }
            }
          }
        })
      : null;

    const importsChart = importsCanvas
      ? createChart(importsCanvas, {
          type: 'bar',
          data: {
            labels: labels.slice(0, -1),
            datasets: [
              {
                data: imports.slice(0, -1),
                backgroundColor: '#3838FF'
              }
            ]
          },
          options: {
            ...commonChartOptions,
            plugins: {
              legend: { display: false },
              datalabels: { display: false }
            },
            scales: {
              x: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } },
              y: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 }, title: { display: true, text: 'Daily lbs in 1,000s', color: tickColor, font: { size: 14 } } }
            }
          }
        })
      : null;

    const operatingChart = operatingCanvas
      ? createChart(operatingCanvas, {
          type: 'bar',
          data: {
            labels: labels.slice(1),
            datasets: [
              {
                label: 'Operating Rate %',
                data: operating.slice(1),
                backgroundColor: '#42b200'
              }
            ]
          },
          options: {
            ...commonChartOptions,
            plugins: {
              legend: { labels: { color: tickColor } },
              datalabels: { display: false }
            },
            scales: {
              x: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } },
              y: { ticks: { color: rsColors.offWhite, callback: (v: any) => v + '%' }, grid: { color: '#4A4A4A', lineWidth: 0.3 }, title: { display: true, text: 'Operating Rate %', color: tickColor, font: { size: 14 } } }
            }
          }
        })
      : null;

    setChartInstances({
      demand: demandChart,
      days: daysChart,
      production: prodChart,
      inventory: inventoryChart,
      imports: importsChart,
      operating: operatingChart
    });
  };

  const updateDataForSelectedDate = (date: string, rows: PPSnapshotData[]) => {
    const sortedRows = [...rows].sort((a, b) => new Date(a.date) > new Date(b.date) ? 1 : -1);
    const currentRow = sortedRows.find(r => r.date === date);
    const currentIndex = sortedRows.findIndex(r => r.date === date);
    const prevRow = sortedRows[currentIndex - 1];
    if (!currentRow) return;

    const ppStats = {
      daysOfInventory: {
        value: Math.round(currentRow.days_of_inventory || 0),
        change: getChange(currentRow.days_of_inventory, prevRow?.days_of_inventory)
      },
      demandRate: {
        value: Math.round((currentRow.demand_rates || 0) * 100),
        change: getChange((currentRow.demand_rates || 0) * 100, (prevRow?.demand_rates || 0) * 100, true)
      },
      exportRate: {
        value: Math.round((currentRow.export_rates || 0) * 100),
        change: getChange((currentRow.export_rates || 0) * 100, (prevRow?.export_rates || 0) * 100, true)
      },
      operatingRate: {
        value: Math.round((currentRow.operating_rates || 0) * 100),
        change: getChange((currentRow.operating_rates || 0) * 100, (prevRow?.operating_rates || 0) * 100, true)
      }
    };

    setStats(ppStats);
    setCommentaryTexts({ supplyDemand: currentRow.pp_supply_demand || '' });

    if (sortedRows.length > 0) {
      const selectedDateObj = new Date(date);
      const recent = sortedRows.filter(r => {
        const d = new Date(r.date);
        const diff =
          (selectedDateObj.getFullYear() - d.getFullYear()) * 12 +
          (selectedDateObj.getMonth() - d.getMonth());
        return diff <= 12 && diff >= 0;
      });
      renderCharts(recent);
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedDate(value);
    onSelectedDateChange?.(value);

    const finalRowForNewDate = finalData.find(f => f.date === value);
    const hasFinalCommentaryForNewDate = finalRowForNewDate && hasCommentaryData(finalRowForNewDate);

    if (commentaryType === 'final' && !hasFinalCommentaryForNewDate) {
      setCommentaryType('preliminary');
      onCommentaryTypeChange?.('preliminary');
    }

    trackUserEvent('monthly_pp_snapshot_date_change', 'PP Snapshot Date Changed', {
      date: value
    });
  };

  const selectedDateHasFinalCommentary = () => {
    const finalRow = finalData.find(f => f.date === selectedDate);
    if (!finalRow) return false;

    const selectedDateObj = new Date(selectedDate);
    const now = new Date();
    const monthsDiff = (now.getFullYear() - selectedDateObj.getFullYear()) * 12 + (now.getMonth() - selectedDateObj.getMonth());

    if (monthsDiff >= 2) {
      return true;
    }

    return hasCommentaryData(finalRow);
  };

  if (loading) {
    return (
      <div className="text-3xl flex text-rs-off-white items-center justify-center h-[500px] w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-5">
        <div className="flex items-center gap-4">
          <select
            value={selectedDate}
            onChange={handleDateChange}
            className="border-rs-jet border-2 p-4 rounded-lg"
          >
            {availableDates.map(date => (
              <option key={date} value={date}>
                {formatDateLabel(date)}
              </option>
            ))}
          </select>
          <select
            value={commentaryType}
            onChange={e => handleCommentaryTypeChange(e.target.value as CommentaryType)}
            className="border-rs-jet border-2 p-4 rounded-lg"
          >
            <option value="preliminary">Preliminary</option>
            {selectedDateHasFinalCommentary() && <option value="final">Final</option>}
          </select>
        </div>
      </div>

      <h3 className="text-xl text-rs-off-white font-bold mb-3">PP Market Statistics</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {renderStatCard('Days of Inventory', stats.daysOfInventory?.value || 0, stats.daysOfInventory?.change || '0')}
        {renderStatCard('Demand Rate', `${stats.demandRate?.value || 0}%`, stats.demandRate?.change || '0')}
        {renderStatCard('Export Rate', `${stats.exportRate?.value || 0}%`, stats.exportRate?.change || '0')}
        {renderStatCard('Operating Rate', `${stats.operatingRate?.value || 0}%`, stats.operatingRate?.change || '0')}
      </div>

      <div className="bg-rs-off-black rounded-lg p-5 mb-8">
        <h4 className="mb-3 text-xl">Market Overview</h4>
        <p
          className="whitespace-pre-line text-base leading-relaxed"
          dangerouslySetInnerHTML={{ __html: formatCommentary(commentaryTexts.supplyDemand || '') }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-h-[400px]">
        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">PP Demand & Capacity</h4>
          <canvas id="ppDemandChart" className="w-full !h-full max-h-[400px]"></canvas>
        </div>
        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">Days of Supply</h4>
          <canvas id="ppDaysSupplyChart" className="w-full !h-full max-h-[400px]"></canvas>
        </div>
        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">Production & Capacity</h4>
          <canvas id="ppProductionChart" className="w-full !h-full max-h-[400px]"></canvas>
        </div>
        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">Inventory Change</h4>
          <canvas id="ppInventoryChart" className="w-full !h-full max-h-[400px]"></canvas>
        </div>
        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">Imports</h4>
          <canvas id="ppImportsChart" className="w-full !h-full max-h-[400px]"></canvas>
        </div>

        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">Operating Rate</h4>
          <canvas id="ppOperatingChart" className="w-full !h-full max-h-[400px]"></canvas>
        </div>
      </div>
    </>
  );
};

export default SnapshotPP;
